import * as React from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, Download, PanelLeftClose, PanelLeftOpen, RotateCcw, Save } from "lucide-react";
import { toast } from "react-toastify";
import { ulid } from "ulid";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/ui/loader";
import { cn } from "@/lib/utils";
import { downloadBlob } from "@/components/feature/pdf/helpers";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  asSaveVersionConflict,
  authoringErrorMessage,
  fetchDocxBytes,
  headDocxUrl,
  useGetAuthoringDocumentQuery,
  useSaveVersionMutation,
  versionDocxUrl,
} from "../documentAuthoring.api";
import { DOCX_MIME_TYPE } from "../documentAuthoring.constraints";
import { isTemplateRunReport, type MergeResolution, type SaveVersionConflict } from "../documentAuthoring.types";
import { InstructionsTab } from "../components/InstructionsTab";
import { MergeConflictDialog } from "../components/MergeConflictDialog";
import { PendingChanges } from "../components/PendingChanges";
import { ProcessingBanner } from "../components/ProcessingBanner";
import { TemplateTab } from "../components/TemplateTab";
import { VersionPicker } from "../components/VersionPicker";
import type { DocxEditorHandle, PendingCounts } from "../editor/editorHandle";
import { LazyDocxEditor } from "../editor/LazyDocxEditor";
import { runBusyMessage, useAuthoringRun } from "../hooks/useAuthoringRun";
import { useUnsavedChangesGuard } from "../hooks/useUnsavedChangesGuard";

type PanelTab = "instructions" | "template";

const PANEL_COLLAPSED_KEY = "authoring-panel-collapsed";

const readPanelCollapsed = (): boolean => {
  try {
    return window.localStorage.getItem(PANEL_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
};

const writePanelCollapsed = (collapsed: boolean) => {
  try {
    window.localStorage.setItem(PANEL_COLLAPSED_KEY, collapsed ? "1" : "0");
  } catch {
    // Per-browser convenience only; ignore storage failures.
  }
};

export const AuthoringView: React.FC = () => {
  const { fseId = "" } = useParams<{ fseId: string }>();
  const { data: doc, isLoading, isError } = useGetAuthoringDocumentQuery(fseId, { skip: !fseId });
  const [saveVersion, { isLoading: saving }] = useSaveVersionMutation();

  const [tab, setTab] = React.useState<PanelTab>("instructions");
  const [panelCollapsed, setPanelCollapsed] = React.useState<boolean>(readPanelCollapsed);
  const togglePanel = () =>
    setPanelCollapsed(collapsed => {
      writePanelCollapsed(!collapsed);
      return !collapsed;
    });
  const [initialBytes, setInitialBytes] = React.useState<Uint8Array | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [selectedVersionId, setSelectedVersionId] = React.useState<string | null>(null);
  const [handle, setHandle] = React.useState<DocxEditorHandle | null>(null);
  const [counts, setCounts] = React.useState<PendingCounts | null>(null);
  const [dirty, setDirty] = React.useState(false);
  const [conflict, setConflict] = React.useState<SaveVersionConflict | null>(null);
  const idempotencyKeyRef = React.useRef<string | null>(null);
  const handleRef = React.useRef<DocxEditorHandle | null>(null);
  handleRef.current = handle;

  const fileName = doc?.name ?? "document.docx";
  const aiAuthor = doc?.ai_author ?? "Pathfinder";
  const headVersionId = doc?.head?.id ?? null;

  const refreshCounts = React.useCallback(() => {
    const current = handleRef.current;
    if (current) setCounts(current.pendingCounts());
  }, []);

  const run = useAuthoringRun({
    fseId,
    fileName,
    editor: handle,
    onLoaded: () => {
      refreshCounts();
      setDirty(true);
    },
  });

  useUnsavedChangesGuard(dirty);

  // Load the head docx once; a version switch reloads through the mounted editor instead.
  React.useEffect(() => {
    if (!doc || initialBytes) return;
    let cancelled = false;
    fetchDocxBytes(headDocxUrl(fseId))
      .then(bytes => {
        if (!cancelled) setInitialBytes(bytes);
      })
      .catch(() => {
        if (!cancelled) setLoadError("The document could not be loaded.");
      });
    return () => {
      cancelled = true;
    };
  }, [doc, fseId, initialBytes]);

  const onReady = React.useCallback((nextHandle: DocxEditorHandle) => {
    setHandle(nextHandle);
    setCounts(nextHandle.pendingCounts());
    setDirty(false);
  }, []);

  const onEditorChange = React.useCallback(() => {
    refreshCounts();
    setDirty(true);
  }, [refreshCounts]);

  const switchVersion = async (versionId: string | null) => {
    if (!handle || run.busy) return;
    if (dirty && !window.confirm("Switching versions discards unsaved changes. Continue?")) return;
    try {
      const bytes = await fetchDocxBytes(versionId ? versionDocxUrl(fseId, versionId) : headDocxUrl(fseId));
      await handle.load(bytes);
      setSelectedVersionId(versionId);
      setDirty(false);
      refreshCounts();
    } catch {
      toast.error("That version could not be loaded.");
    }
  };

  // Back out every unsaved edit (typing, accepts/rejects, AI runs) by reloading the version on screen.
  const discardChanges = async () => {
    if (!handle || run.busy) return;
    if (!window.confirm("Discard all unsaved changes and reload the saved version?")) return;
    try {
      const bytes = await fetchDocxBytes(
        selectedVersionId ? versionDocxUrl(fseId, selectedVersionId) : headDocxUrl(fseId),
      );
      await handle.load(bytes);
      setDirty(false);
      refreshCounts();
    } catch {
      toast.error("The saved version could not be reloaded.");
    }
  };

  const save = async (resolutions?: MergeResolution[]) => {
    if (!handle || !doc) return;
    // One idempotency key per save attempt; a conflict resubmit reuses it.
    const idempotencyKey = resolutions ? (idempotencyKeyRef.current ?? ulid()) : (idempotencyKeyRef.current = ulid());
    try {
      const file = await handle.save();
      const result = await saveVersion({
        fseId,
        file,
        fileName,
        base_version_id: selectedVersionId ?? headVersionId ?? "root",
        resolutions,
        idempotency_key: idempotencyKey,
      }).unwrap();
      setConflict(null);
      setDirty(false);
      setSelectedVersionId(null);
      if (result.merged) {
        toast.info(`Merged with the latest changes into version ${result.version.version_no}.`);
        await handle.load(await fetchDocxBytes(versionDocxUrl(fseId, result.version.id)));
        setDirty(false);
        refreshCounts();
      } else {
        toast.success(`Saved version ${result.version.version_no}. Processing continues in the background.`);
      }
    } catch (error) {
      const merge = asSaveVersionConflict(error);
      if (merge) {
        setConflict(merge);
        return;
      }
      toast.error(authoringErrorMessage(error, "The version could not be saved."));
    }
  };

  const download = async () => {
    if (!handle) return;
    const versionNo = doc?.head?.version_no;
    const base = fileName.replace(/\.docx$/i, "");
    downloadBlob(
      new Blob([(await handle.save()) as BlobPart], { type: DOCX_MIME_TYPE }),
      `${base}${versionNo ? `-v${versionNo}` : ""}.docx`,
    );
  };

  const busyMessage = runBusyMessage(run.state, aiAuthor);
  const report = run.state.report;
  const instructionsReport = report && !isTemplateRunReport(report) ? report : null;
  const templateReport = report && isTemplateRunReport(report) ? report : null;
  const runError = run.state.status === "failed" ? run.state.error : null;

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader className="h-8 w-8 text-coral" />
      </div>
    );
  }

  if (isError || !doc) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3">
        <p className="text-sm text-gray-600">This document is not available for authoring.</p>
        <Button variant="outline" asChild>
          <Link to="/drive">Back to Drive</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="-mx-4 -my-8 flex h-screen flex-col overflow-hidden bg-ivory-mist-100 sm:-mx-6 lg:-mx-8">
      <header className="flex flex-wrap items-center gap-3 border-b border-desert-sand-300 bg-white px-4 py-2">
        <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
          <Link to="/drive" aria-label="Back to Drive">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="truncate text-sm font-semibold text-ui-heading-text" title={doc.name}>
          {doc.name}
        </h1>
        <Badge variant="secondary">{doc.head ? `v${doc.head.version_no}` : "Original"}</Badge>
        {dirty && <Badge variant="review-amber">Unsaved changes</Badge>}
        <VersionPicker
          versions={doc.versions}
          selectedVersionId={selectedVersionId}
          headVersionId={headVersionId}
          disabled={!handle || run.busy || saving}
          onSelect={versionId => void switchVersion(versionId)}
        />
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void download()} disabled={!handle || run.busy}>
            <Download className="mr-1 h-4 w-4" /> Download .docx
          </Button>
          {dirty && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void discardChanges()}
              disabled={!handle || run.busy || saving}
            >
              <RotateCcw className="mr-1 h-4 w-4" /> Discard changes
            </Button>
          )}
          <Button
            variant="coral"
            size="sm"
            onClick={() => void save()}
            disabled={!handle || run.busy || saving || !dirty}
          >
            <Save className="mr-1 h-4 w-4" /> {saving ? "Saving…" : "Save version"}
          </Button>
        </div>
      </header>

      {doc.pending_version && <ProcessingBanner pendingVersion={doc.pending_version} />}

      <div className="flex min-h-0 flex-1">
        <Tabs
          value={tab}
          onValueChange={value => setTab(value as PanelTab)}
          className={cn(
            "flex min-w-0 flex-col border-r border-desert-sand-300 bg-white",
            panelCollapsed ? "w-12 shrink-0" : "w-2/5",
          )}
        >
          {panelCollapsed ? (
            <div className="flex flex-col items-center gap-3 py-3">
              <Button variant="ghost" size="xs" aria-label="Expand panel" title="Expand panel" onClick={togglePanel}>
                <PanelLeftOpen className="h-4 w-4" />
              </Button>
              {counts && counts.changes + counts.comments > 0 && (
                <span
                  className="rounded-full bg-coral px-1.5 py-0.5 text-xxs font-semibold text-white"
                  title={`${counts.changes} changes, ${counts.comments} comments to review`}
                >
                  {counts.changes + counts.comments}
                </span>
              )}
            </div>
          ) : (
            <>
              <div className="m-3 mb-0 flex items-start justify-between gap-2">
                <TabsList className="grid w-fit grid-cols-2">
                  <TabsTrigger value="instructions">Instructions</TabsTrigger>
                  <TabsTrigger value="template">Template</TabsTrigger>
                </TabsList>
                <Button
                  variant="ghost"
                  size="xs"
                  aria-label="Collapse panel"
                  title="Collapse panel"
                  onClick={togglePanel}
                >
                  <PanelLeftClose className="h-4 w-4" />
                </Button>
              </div>
              <PendingChanges
                counts={counts}
                disabled={!handle || run.busy}
                onAcceptAll={() => {
                  handle?.acceptAll();
                  refreshCounts();
                }}
                onRejectAll={() => {
                  handle?.rejectAll();
                  refreshCounts();
                }}
              />
              <div className="min-h-0 flex-1 overflow-y-auto">
                <TabsContent value="instructions" className="mt-0">
                  <InstructionsTab
                    aiAuthor={aiAuthor}
                    busy={run.busy || saving}
                    running={run.busy && run.state.kind === "instructions"}
                    editorReady={handle !== null}
                    error={run.state.kind === "instructions" ? runError : null}
                    report={instructionsReport}
                    onRun={instructions => void run.start({ kind: "instructions", instructions })}
                    onCancel={run.cancel}
                  />
                </TabsContent>
                <TabsContent value="template" className="mt-0">
                  <TemplateTab
                    aiAuthor={aiAuthor}
                    busy={run.busy || saving}
                    running={run.busy && run.state.kind === "template"}
                    editorReady={handle !== null}
                    error={run.state.kind === "template" ? runError : null}
                    statuses={run.state.kind === "template" ? run.state.checks : []}
                    report={templateReport}
                    onRun={(templateId, checkCount) =>
                      void run.start({ kind: "template", template_id: templateId, checkCount })
                    }
                  />
                </TabsContent>
              </div>
            </>
          )}
        </Tabs>

        <main className="relative flex min-w-0 flex-1 flex-col">
          {loadError ? (
            <p className="p-6 text-sm text-danger" role="alert">
              {loadError}
            </p>
          ) : initialBytes ? (
            <LazyDocxEditor
              bytes={initialBytes}
              author={doc.ai_author}
              aiAuthor={doc.ai_author}
              onReady={onReady}
              onChange={onEditorChange}
            />
          ) : (
            <div className="flex flex-1 items-center justify-center">
              <Loader className="h-8 w-8 text-coral" />
            </div>
          )}
          {busyMessage && (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-[1px]"
              aria-live="polite"
            >
              <div className="flex max-w-sm flex-col items-center gap-3 rounded-lg border border-desert-sand-300 bg-white p-6 text-center shadow-lg">
                <Loader className="h-8 w-8 text-coral" />
                <p className="text-sm text-ui-paragraph-text">{busyMessage}</p>
                <Button variant="link" size="sm" onClick={run.cancel}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </main>
      </div>

      <MergeConflictDialog
        conflict={conflict}
        submitting={saving}
        onCancel={() => setConflict(null)}
        onResubmit={resolutions => void save(resolutions)}
      />
    </div>
  );
};
